// src/components/ParticlesBackground.jsx
import React, { useCallback, useMemo } from "react";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

const cssVar = (name, fallback) =>
  typeof window !== "undefined"
    ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
    : fallback;

export default function ParticlesBackground({ id = "global-particles", theme = "light" }) {
  const init = useCallback(async (engine) => { await loadSlim(engine); }, []);

  const colors = useMemo(() => {
    const primary = cssVar("--brand-primary", "#0d6efd");
    const accent  = cssVar("--brand-accent",  "#ff4d8d");
    const link    = theme === "dark" ? "rgba(255,255,255,.25)" : "rgba(0,0,0,.25)";
    return { primary, accent, link };
  }, [theme]);

  const options = useMemo(() => ({
    fpsLimit: 60,
    fullScreen: { enable: false },          // we position it manually with CSS
    background: { color: { value: "transparent" } },
    detectRetina: true,
    interactivity: {
      events: {
        onHover: { enable: true, mode: "repulse" },
        onClick: { enable: true, mode: "push" },
        resize: true,
      },
      modes: {
        repulse: { distance: 120, duration: 0.35 },
        push: { quantity: 2 },
      },
    },
    particles: {
      number: { value: theme === "dark" ? 55 : 75, density: { enable: true, area: 900 } },
      color: { value: [colors.primary, colors.accent] },
      links: { enable: true, color: colors.link, distance: 140, opacity: 0.35, width: 1 },
      move: { enable: true, speed: theme === "dark" ? 0.8 : 1.0, outModes: { default: "out" } },
      opacity: { value: 0.55 },
      size: { value: { min: 1, max: 3 } },
    },
    responsive: [
      { maxWidth: 992, options: { particles: { number: { value: theme === "dark" ? 40 : 50 } } } },
      { maxWidth: 576, options: { particles: { number: { value: theme === "dark" ? 24 : 32 } } } },
    ],
  }), [colors, theme]);

  return (
    <>
      <Particles id={id} className="global-particles" init={init} options={options} />
      <style>{`
        .global-particles{
          position: fixed; inset: 0;
          width: 100vw; height: 100vh;
          z-index: 0; pointer-events: none;   /* never block clicks */
        }
      `}</style>
    </>
  );
}
